import React, { useEffect, useState, useRef } from 'react';
import { X, ArrowUp, ArrowDown, Loader2, ListOrdered, Check, AlertCircle } from 'lucide-react';
import { useMenuCategories, useMenuItems } from '../../lib/menu-hooks';
import { cn } from '../../lib/utils';
import { MenuItem } from '../../lib/types';
import { supabase } from '../../lib/supabase';
interface MenuReorderDrawerProps {
  restaurantId: string;
  isOpen: boolean;
  onClose: () => void;
}
export function MenuReorderDrawer({
  restaurantId,
  isOpen,
  onClose
}: MenuReorderDrawerProps) {
  const {
    categories,
    loading: catLoading
  } = useMenuCategories(restaurantId);
  const {
    items,
    loading: itemsLoading,
    refetch: refetchItems
  } = useMenuItems(restaurantId);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [localItems, setLocalItems] = useState<MenuItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // True whenever the user has made changes that haven't been saved yet.
  // While `isDirty` is true, we never overwrite local order with server data.
  const [isDirty, setIsDirty] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  // Avoid wiping the user's draft when switching categories: we only resync
  // local items from server when the category actually changes OR when the
  // drawer is opened fresh OR right after a successful save.
  const lastSyncedCategoryRef = useRef<string | null>(null);
  // Pick the first category by default when the drawer mounts/opens.
  useEffect(() => {
    if (categories.length > 0 && !selectedCategoryId) {
      setSelectedCategoryId(categories[0].id);
    }
  }, [categories, selectedCategoryId]);
  // Resync local items from server only when the category changes, or when
  // the set of item IDs in this category genuinely changes (e.g. an item
  // was added / removed elsewhere). While the user has unsaved changes,
  // skip resync entirely so we never overwrite their draft order.
  useEffect(() => {
    if (!selectedCategoryId) {
      setLocalItems([]);
      lastSyncedCategoryRef.current = null;
      return;
    }
    if (isDirty) return;
    const catItems = items.filter(i => i.category_id === selectedCategoryId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const categoryChanged = lastSyncedCategoryRef.current !== selectedCategoryId;
    setLocalItems(prev => {
      if (categoryChanged) {
        lastSyncedCategoryRef.current = selectedCategoryId;
        return catItems;
      }
      const prevIds = prev.map(i => i.id);
      const nextIds = catItems.map(i => i.id);
      const sameSet = prevIds.length === nextIds.length && prevIds.every(id => nextIds.includes(id));
      if (sameSet && prev.length > 0) {
        // Same items, possibly refreshed fields — keep user's local order.
        const byId = new Map(catItems.map(i => [i.id, i]));
        return prev.map(p => byId.get(p.id) || p);
      }
      return catItems;
    });
  }, [selectedCategoryId, items, isDirty]);
  // Reset transient UI flags whenever the drawer is closed/opened.
  useEffect(() => {
    if (!isOpen) {
      setJustSaved(false);
      setSaveError(null);
      setEditingId(null);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const isLoading = catLoading || itemsLoading;
  // ── Local-only reordering helpers (no DB writes) ─────────────────────
  const handleMove = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0 || direction === 'down' && index === localItems.length - 1) return;
    const newItems = [...localItems];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    const temp = newItems[index];
    newItems[index] = newItems[swapIndex];
    newItems[swapIndex] = temp;
    setLocalItems(newItems.map((item, i) => ({
      ...item,
      sort_order: i + 1
    })));
    setIsDirty(true);
    setJustSaved(false);
    setSaveError(null);
  };
  const handlePositionSubmit = (itemId: string) => {
    const newPos = parseInt(editValue, 10);
    if (isNaN(newPos) || newPos < 1 || newPos > localItems.length) {
      setEditingId(null);
      return;
    }
    const currentIndex = localItems.findIndex(i => i.id === itemId);
    if (currentIndex === -1 || currentIndex === newPos - 1) {
      setEditingId(null);
      return;
    }
    const newItems = [...localItems];
    const [moved] = newItems.splice(currentIndex, 1);
    newItems.splice(newPos - 1, 0, moved);
    setLocalItems(newItems.map((item, i) => ({
      ...item,
      sort_order: i + 1
    })));
    setEditingId(null);
    setIsDirty(true);
    setJustSaved(false);
    setSaveError(null);
  };
  // ── Persist the current local order to the DB ─────────────────────────
  const handleSave = async () => {
    if (saving || !isDirty || localItems.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setJustSaved(false);
    try {
      // Sequential updates → simpler RLS auditing and avoids any race where
      // a partial parallel set could be picked up by a refresh mid-flight.
      for (let i = 0; i < localItems.length; i++) {
        const item = localItems[i];
        const {
          error
        } = await supabase.from('menu_items').update({
          sort_order: i + 1
        }).eq('id', item.id);
        if (error) throw error;
      }
      // Pull the freshly-saved order back from the server so what we show
      // matches exactly what the customer's menu will show.
      await refetchItems();
      setIsDirty(false);
      setJustSaved(true);
      // Hide the "Guardado" pill after a few seconds.
      setTimeout(() => setJustSaved(false), 2500);
    } catch (err: any) {
      console.error('[NOIZA][menu-reorder] save failed:', err);
      setSaveError(err?.message || 'No se pudo guardar el orden.');
    } finally {
      setSaving(false);
    }
  };
  // Guard close → prompt if there are unsaved changes.
  const handleRequestClose = () => {
    if (isDirty) {
      const ok = window.confirm('Tenés cambios sin guardar. ¿Querés descartarlos?');
      if (!ok) return;
      setIsDirty(false);
      // Force a fresh resync next time the drawer opens.
      lastSyncedCategoryRef.current = null;
    }
    onClose();
  };
  return <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] transition-opacity" onClick={handleRequestClose} data-id="element-790" />

      <div className="fixed inset-y-0 right-0 w-full sm:w-[500px] bg-[#141414] border-l border-white/10 shadow-2xl z-[101] flex flex-col transform transition-transform duration-300" data-id="element-791">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10 shrink-0" data-id="element-792">
          <div className="flex items-center gap-3" data-id="element-793">
            <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center" data-id="element-794">
              <ListOrdered className="w-5 h-5 text-white/70" data-id="element-795" />
            </div>
            <div data-id="element-796">
              <h2 className="text-lg font-bold text-white leading-tight" data-id="element-797">
                Ordenar Productos
              </h2>
              <p className="text-xs text-white/40" data-id="element-798">
                Acomodá el orden y luego presioná{' '}
                <span className="text-white/70 font-semibold" data-id="element-799">
                  Guardar cambios
                </span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-800">
            {isDirty && !saving && <span className="hidden sm:inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold uppercase tracking-wider" data-id="element-801">
                Sin guardar
              </span>}
            {justSaved && !isDirty && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[10px] font-bold uppercase tracking-wider" data-id="element-802">
                <Check className="w-3 h-3" data-id="element-803" /> Guardado
              </span>}
            <button onClick={handleRequestClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors" data-id="element-804">
              <X className="w-5 h-5" data-id="element-805" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col" data-id="element-806">
          {isLoading && localItems.length === 0 ? <div className="flex-1 flex items-center justify-center" data-id="element-807">
              <Loader2 className="w-8 h-8 text-white/20 animate-spin" data-id="element-808" />
            </div> : <>
              {/* Category Selector */}
              <div className="p-4 border-b border-white/10 shrink-0 overflow-x-auto" data-id="element-809">
                <div className="flex gap-2 min-w-max pb-1" data-id="element-810">
                  {categories.map(cat => {
                const isActive = selectedCategoryId === cat.id;
                return <button key={cat.id} onClick={() => {
                  if (isActive) return;
                  if (isDirty) {
                    const ok = window.confirm('Tenés cambios sin guardar en esta categoría. ¿Descartarlos?');
                    if (!ok) return;
                    setIsDirty(false);
                  }
                  setSelectedCategoryId(cat.id);
                  setEditingId(null);
                  setJustSaved(false);
                  setSaveError(null);
                }} className={cn('px-4 py-2 rounded-full text-sm font-bold transition-colors whitespace-nowrap', isActive ? 'bg-white text-black' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white')} data-id="element-811">
                        {cat.name}
                      </button>;
              })}
                </div>
              </div>

              {/* Items List */}
              <div className="flex-1 overflow-y-auto p-4" data-id="element-812">
                {localItems.length === 0 ? <div className="text-center text-white/40 py-8" data-id="element-813">
                    No hay productos en esta categoría
                  </div> : <div className="flex flex-col gap-2" data-id="element-814">
                    {localItems.map((item, index) => <div key={item.id} className="flex items-center gap-3 p-3 rounded-xl border bg-white/5 border-white/10 hover:border-white/20 transition-colors" data-id="element-815">
                        <div className="flex flex-col gap-1 shrink-0" data-id="element-816">
                          <button onClick={() => handleMove(index, 'up')} disabled={index === 0 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-817">
                            <ArrowUp className="w-3.5 h-3.5" data-id="element-818" />
                          </button>
                          <button onClick={() => handleMove(index, 'down')} disabled={index === localItems.length - 1 || saving} className="p-1 rounded-lg text-white/40 hover:text-white hover:bg-white/10 disabled:opacity-30 transition-colors" data-id="element-819">
                            <ArrowDown className="w-3.5 h-3.5" data-id="element-820" />
                          </button>
                        </div>

                        {editingId === item.id ? <form onSubmit={e => {
                  e.preventDefault();
                  handlePositionSubmit(item.id);
                }} className="shrink-0" data-id="element-821">
                            <input type="number" min={1} max={localItems.length} value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={() => handlePositionSubmit(item.id)} autoFocus className="w-10 h-8 rounded-lg bg-white text-black text-center text-sm font-bold outline-none" data-id="element-822" />
                          </form> : <button onClick={() => {
                  setEditingId(item.id);
                  setEditValue(String(index + 1));
                }} className="w-10 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 hover:bg-white/20 transition-colors cursor-text" title="Clic para escribir posición" data-id="element-823">
                            <span className="text-sm font-bold text-white/60" data-id="element-824">
                              {index + 1}
                            </span>
                          </button>}

                        <div className="flex-1 min-w-0" data-id="element-825">
                          <div className="font-bold text-white truncate" data-id="element-826">
                            {item.name}
                          </div>
                        </div>
                      </div>)}
                  </div>}
              </div>

              {/* Sticky Save Footer */}
              <div className="border-t border-white/10 bg-[#0F0F0F] p-4 shrink-0 space-y-2" data-id="element-827">
                {saveError && <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-xs" data-id="element-828">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-829" />
                    <span data-id="element-830">{saveError}</span>
                  </div>}
                <button onClick={handleSave} disabled={!isDirty || saving || localItems.length === 0} className={cn('w-full h-12 rounded-xl text-sm font-black flex items-center justify-center gap-2 transition-all', isDirty && !saving ? 'bg-amber-500 hover:bg-amber-400 text-black active:scale-95 shadow-lg' : 'bg-white/5 text-white/30 cursor-not-allowed')} data-id="element-831">
                  {saving ? <>
                      <Loader2 className="w-4 h-4 animate-spin" data-id="element-832" />
                      Guardando...
                    </> : justSaved && !isDirty ? <>
                      <Check className="w-4 h-4" data-id="element-833" />
                      Guardado
                    </> : isDirty ? '💾 Guardar cambios' : 'Sin cambios para guardar'}
                </button>
                <p className="text-[10px] text-white/30 text-center leading-snug" data-id="element-834">
                  Los cambios solo se verán en el menú del cliente después de
                  guardarlos.
                </p>
              </div>
            </>}
        </div>
      </div>
    </>;
}